import type { AppListenerEffectAPI, AppStartListening, RootState } from '@/redux/store';
import { Action, createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';
import { initMapSizeAndModelId } from '@/redux/map/map.thunks';
import { getModelElementsForModel } from '@/redux/modelElements/modelElements.thunks';
import { getModelIdFromAction } from '@/redux/map/middleware/getModelIdFromAction';
import { modelElementsByModelIdSelector } from '@/redux/modelElements/modelElements.selector';
import { ZERO } from '@/constants/common';
import { currentModelIdSelector, modelsDataSelector } from '@/redux/models/models.selectors';
import { showToast } from '@/utils/showToast';
import {
  closeMapAndSetMainMapActive,
  openMapAndOrSetActiveIfSelected,
  openMapAndSetActive,
  setActiveMap,
} from '../map.slice';

export const openMapListenerMiddleware = createListenerMiddleware();

const startListening = openMapListenerMiddleware.startListening as AppStartListening;

function checkModelElementsOutsideOfBounds(modelId: number | null, state: RootState): void {
  if (modelId) {
    const elements = modelElementsByModelIdSelector(state, modelId);
    const models = modelsDataSelector(state);
    const model = models.filter(m => m.id === modelId)[ZERO];

    const number = elements.filter(element => {
      return (
        element.x < ZERO || element.y < ZERO || element.x > model.width || element.y > model.height
      );
    }).length;
    if (number > ZERO) {
      showToast({
        type: 'error',
        message: 'One or more elements of a diagram is out of bounds.',
      });
    }
  }
}

export const openMapListenerMiddlewareListener = async (
  action: Action,
  { getState }: AppListenerEffectAPI,
): Promise<void> => {
  const state = getState();

  const modelId = getModelIdFromAction(action);
  checkModelElementsOutsideOfBounds(modelId, state);
};

startListening({
  matcher: isAnyOf(
    setActiveMap,
    openMapAndSetActive,
    openMapAndOrSetActiveIfSelected,
    closeMapAndSetMainMapActive,
    initMapSizeAndModelId.fulfilled,
  ),
  effect: openMapListenerMiddlewareListener,
});

export const downloadMapListenerMiddlewareListener = async (
  action: Action,
  { getState }: AppListenerEffectAPI,
): Promise<void> => {
  const state = getState();

  const meta = 'meta' in action ? (action.meta as object) : {};
  const modelId = 'arg' in meta ? (meta.arg as number) : null;

  const currentModelId = currentModelIdSelector(state);
  if (currentModelId === modelId) {
    checkModelElementsOutsideOfBounds(modelId, state);
  }
};

startListening({
  matcher: isAnyOf(getModelElementsForModel.fulfilled),
  effect: downloadMapListenerMiddlewareListener,
});
